import { inject, Injectable, NgZone } from '@angular/core';
import { AuthTokenService } from './auth-token.service';

const REQUEST_TOKEN = 'jf-reporting:request-token';
const TOKEN_REPLY = 'jf-reporting:token';
const FORCE_REAUTH = 'jf-reporting:force-reauth';

const REPLY_TIMEOUT_MS = 4000;
// Refresh this long before the token's `exp` so a request never goes out stale.
const PROACTIVE_LEAD_MS = 60_000;

/**
 * Keeps the SPA's JWT alive by talking to the host window that embeds us.
 * The SPA never did its own OIDC flow — the host handed it a token via `?token=`,
 * so refresh means asking the host (parent/opener) for its current token, and
 * falling back to the host's full re-auth when that can't help.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
    private auth = inject(AuthTokenService);
    private zone = inject(NgZone);

    private inFlight: Promise<boolean> | null = null;
    private proactiveTimer: ReturnType<typeof setTimeout> | null = null;

    // Adopt the host's current token if it's newer than ours. De-duped across concurrent callers.
    public refreshFromHost(): Promise<boolean> {
        if (!this.inFlight) {
            this.inFlight = this.doRefresh().finally(() => (this.inFlight = null));
        }
        return this.inFlight;
    }

    // Last resort: ask the host to run its full re-auth (reloads the embedding view).
    public forceReauth(): void {
        this.host?.postMessage({ type: FORCE_REAUTH }, this.hostOrigin());
    }

    // Schedule an automatic refresh shortly before the current token expires.
    public scheduleProactiveRefresh(): void {
        if (this.proactiveTimer) {
            clearTimeout(this.proactiveTimer);
            this.proactiveTimer = null;
        }
        if (!this.host) return;
        const expiry = this.auth.expiresAtMs;
        if (expiry == null) return;

        const delay = Math.max(0, expiry - Date.now() - PROACTIVE_LEAD_MS);
        this.zone.runOutsideAngular(() => {
            this.proactiveTimer = setTimeout(
                () => this.zone.run(() => void this.refreshFromHost()),
                delay,
            );
        });
    }

    // The window that embedded us (iframe parent or pop-out opener); null when standalone.
    private get host(): Window | null {
        if (window.opener) return window.opener as Window;
        if (window.parent && window.parent !== window) return window.parent;
        return null;
    }

    private async doRefresh(): Promise<boolean> {
        const host = this.host;
        if (!host) return false;

        const token = await this.requestToken(host);
        if (!token || token === this.auth.token) return false;

        this.auth.setToken(token);
        this.scheduleProactiveRefresh();
        return true;
    }

    private requestToken(host: Window): Promise<string | null> {
        return new Promise((resolve) => {
            let done = false;
            const finish = (value: string | null) => {
                if (done) return;
                done = true;
                window.removeEventListener('message', onMessage);
                resolve(value);
            };
            const onMessage = (e: MessageEvent) => {
                if (e.source !== host) return;
                const data = e.data as { type?: string; token?: string | null };
                if (data?.type !== TOKEN_REPLY) return;
                finish(data.token ?? null);
            };
            window.addEventListener('message', onMessage);
            host.postMessage({ type: REQUEST_TOKEN }, this.hostOrigin());
            setTimeout(() => finish(null), REPLY_TIMEOUT_MS);
        });
    }

    private hostOrigin(): string {
        try {
            return document.referrer ? new URL(document.referrer).origin : '*';
        } catch {
            return '*';
        }
    }
}
